// generateNCHLQRCode.js (renamed to NCHLQR.js)
import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { toast } from "react-toastify";
import QRCode from "react-qr-code";
import nchlLogo from "assets/nchl/logo.png";
import ErrorAlert from "components/Shared/ErrorAlert";
import {
  useGenerateNCHLPaymentQRMutation,
  useCheckNCHLPaymentStatusMutation,
} from "store/services/PaymentsApi/paymentApi";

// NCHL QR is valid for 150s (matches the server-side websocket timer).
const QR_VALIDITY_SECONDS = 150;

const formatTime = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(s / 60);
  const seconds = s % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const NCHLQR = forwardRef(
  ({ amount = 0, addedUser, shipmentId, priceIds = [], onSuccess = () => { } }, ref) => {
    const [open, setOpen] = useState(false);
    const [error, setError] = useState(null);
    const [isQr, setIsQr] = useState(null);
    const [secondsLeft, setSecondsLeft] = useState(QR_VALIDITY_SECONDS);
    const tickRef = useRef(null);

    // RTK Query hooks. The second tuple element (the result object)
    // carries the .reset() method — the trigger function does NOT — so
    // we have to keep a reference to the whole result object, not just
    // destructure its fields. (Calling .reset() on the trigger throws
    // `checkPaymentStatus.reset is not a function`.)
    const [generateQr, generateQrResult] =
      useGenerateNCHLPaymentQRMutation();
    const { data: qrData, isLoading: loadingQR } = generateQrResult;
    const [
      checkPaymentStatus,
      checkPaymentStatusResult,
    ] = useCheckNCHLPaymentStatusMutation();
    const { data: paymentStatus, isLoading: checkingStatus } =
      checkPaymentStatusResult;

    // Tracks whether onSuccess has already fired for the current payment,
    // so we never call it twice (race window: the success effect and the
    // "Complete Payment" button can both observe paymentSuccess === true
    // in the same render frame). Only the effect calls onSuccess; the
    // button just closes the dialog. Reset in closeAndReset so a new
    // payment flow can notify again.
    const onSuccessCalledRef = useRef(false);

    // Start / stop the countdown timer when the QR dialog opens / closes,
    // and reset it whenever a new QR is generated.
    useEffect(() => {
      if (!open) {
        if (tickRef.current) {
          clearInterval(tickRef.current);
          tickRef.current = null;
        }
        return;
      }
      setSecondsLeft(QR_VALIDITY_SECONDS);
      tickRef.current = setInterval(() => {
        setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
      return () => {
        if (tickRef.current) {
          clearInterval(tickRef.current);
          tickRef.current = null;
        }
      };
    }, [open, qrData?.validationTraceId]);

    const qrExpired = secondsLeft <= 0;

    // Handle QR generation
    const handleGenerateQR = async () => {
      if (!amount || !priceIds || priceIds.length === 0) {
        toast.error("Amount and Price ID are required for QR generation");
        return null;
      }

      setError(null);
      try {
        setOpen(true);

        // Reuse the existing QR only if we have one AND the amount
        // matches AND the previous QR hasn't expired. Otherwise force
        // generation of a fresh QR (and a fresh validationTraceId) so we
        // never hand back a dead QR to the caller.
        const canReuse =
          qrData &&
          amount === isQr?.amount &&
          !qrExpired;

        if (canReuse) {
          // Do NOT reset the countdown here — the reused QR has whatever
          // validity is left on its real timer, and we already track it
          // in `secondsLeft`. Resetting would overstate remaining time.
          return isQr.validationTraceId;
        }

        // Fresh-generation path: reset the displayed countdown to match
        // the new 150s server-side window.
        setSecondsLeft(QR_VALIDITY_SECONDS);

        const res = await generateQr({
          amount,
          shipmentId,
          priceIds,
          addedUser,
        }).unwrap();

        const traceId = res.validationTraceId;
        setIsQr({
          validationTraceId: traceId,
          amount,
          priceIds,
        });

        return traceId;
      } catch (error) {
        setError(error);
        toast.error(error?.data?.message || "Failed to generate QR code");
        return null;
      }
    };

    // Handle payment status check
    const handleCheckStatus = useCallback(async () => {
      if (loadingQR || !qrData?.validationTraceId) return;
      try {
        await checkPaymentStatus({
          validationTraceId: qrData.validationTraceId,
        }).unwrap();
      } catch (error) {
        setError(error);
        toast.error(error?.data?.message || "Failed to check payment status");
      }
    }, [checkPaymentStatus, loadingQR, qrData?.validationTraceId]);

    // Determine payment success
    const paymentSuccess = useMemo(
      () => paymentStatus?.debitStatus === "000",
      [paymentStatus?.debitStatus],
    );

    // Single source of truth for "close the dialog and reset everything".
    // Every close path — backdrop/Escape onClose, the "Close" button, the
    // imperative handleCloseQR ref, AND the payment-success effect — goes
    // through this. Closing always means "next generate is a fresh start"
    // so we never hand back the just-paid validationTraceId to the next
    // caller (a real financial-integrity risk if the caller re-uses the
    // component with the same amount for a new transaction).
    //
    // Importantly: we also call .reset() on both RTK Query mutations so
    // stale `paymentStatus` / `qrData` from the previous payment can't
    // bleed into the next payment flow. RTK Query mutations persist their
    // last result until re-triggered or explicitly reset — without .reset()
    // a second `handleGenerateQR()` would see the old debitStatus === "000"
    // and the success effect would instantly fire onSuccess for a payment
    // that never happened.
    const closeAndReset = useCallback(() => {
      setOpen(false);
      setIsQr(null);
      setError(null);
      setSecondsLeft(QR_VALIDITY_SECONDS);
      onSuccessCalledRef.current = false;
      // Order matters: clear RTK state first so any in-flight success
      // resolution that races the close can't repopulate paymentStatus.
      // These .reset() calls are on the result objects (second tuple
      // element), not on the trigger functions.
      checkPaymentStatusResult.reset();
      generateQrResult.reset();
    }, [checkPaymentStatusResult, generateQrResult]);

    // Handle success callback
    React.useEffect(() => {
      // Guard with `open` so a checkPaymentStatus that resolves after the
      // user closed the dialog (e.g. dismissed via backdrop/Escape or
      // imperative handleCloseQR) does NOT fire onSuccess on a dialog
      // the user has already walked away from.
      if (!paymentSuccess || !open) return;
      if (onSuccessCalledRef.current) return;
      onSuccessCalledRef.current = true;
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      // Capture paymentStatus into a local before resetting state. Then
      // reset first, THEN notify the parent. If the parent's onSuccess
      // synchronously triggers another handleGenerateQR() (e.g. a parent
      // batching sequential shipment payments), that re-entrant call sets
      // fresh state AFTER closeAndReset() runs — so the new payment's
      // open/trace/mutations aren't wiped by our reset. Calling onSuccess
      // first (the old order) would have closeAndReset() stomping the
      // freshly-set state of the chained call.
      const paidStatus = paymentStatus;
      closeAndReset();
      onSuccess(paidStatus);
    }, [paymentSuccess, open, onSuccess, paymentStatus, closeAndReset]);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      handleGenerateQR,
      handleCloseQR: closeAndReset,
    }));

    // Render QR code
    const renderQR = useMemo(() => {
      if (loadingQR) {
        return <CircularProgress size={120} />;
      }

      if (!qrData?.qrString) {
        return null;
      }

      return (
        <div className="relative flex flex-col gap-3 text-center">
          <img
            src={nchlLogo}
            alt="NCHL Logo"
            className="mx-auto aspect-auto h-20"
          />
          {qrData?.qrString ? (
            // Render the QR locally with react-qr-code — the previous
            // external-service approach (qrcode.nepalcanmove.com) was
            // failing to load in this environment, leaving the alt text
            // visible instead of the image. react-qr-code draws an
            // inline SVG so it works offline with no third-party calls.
            <div className="mx-auto rounded border border-gray-200 bg-white p-3">
              <QRCode
                value={qrData.qrString}
                size={256}
                level="M"
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              />
            </div>
          ) : (
            <div className="flex h-72 w-72 mx-auto items-center justify-center rounded border border-dashed border-red-400 text-sm text-red-600">
              QR string missing from response — please close and try again
            </div>
          )}
          <div className="text-sm">Scan QR to pay Rs. {amount?.toFixed(2)}</div>

          {/* Countdown timer for QR validity (server enforces 150s). */}
          {/* No explicit text color in active state so it inherits the
              theme (white in dark mode, like the dialog title). */}
          <div
            className={`text-sm font-medium ${qrExpired ? "text-red-600 dark:text-red-400" : ""
              }`}
          >
            {qrExpired
              ? "QR expired. Please close and generate a new one."
              : `QR expires in ${formatTime(secondsLeft)}`}
          </div>

          {checkingStatus && (
            <div className="absolute inset-0 flex items-center justify-center rounded bg-gray-800 bg-opacity-20 backdrop-blur">
              <CircularProgress color="success" size={120} />
            </div>
          )}

          {!checkingStatus && paymentSuccess && (
            <div className="absolute -right-2 -top-2">
              <CheckCircle fontSize="large" color="success" />
            </div>
          )}

          <Button
            variant="outlined"
            className="mt-2"
            onClick={handleCheckStatus}
            disabled={checkingStatus || qrExpired}
          >
            Check Payment
          </Button>
        </div>
      );
    }, [
      amount,
      checkingStatus,
      handleCheckStatus,
      loadingQR,
      paymentSuccess,
      qrData?.qrString,
      qrExpired,
      secondsLeft,
    ]);

    return (
      <Dialog
        open={open}
        onClose={closeAndReset}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Pay Via NCHL</DialogTitle>
        <DialogContent>
          <div className="min-h-44 flex flex-col items-center gap-3">
            <ErrorAlert
              error={!!error ? { message: "Invalid Payment" } : null}
            />
            {renderQR}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAndReset}>Close</Button>
          <Button
            variant="contained"
            onClick={
              paymentSuccess ? closeAndReset : handleCheckStatus
            }
            disabled={(!paymentSuccess && qrExpired) || checkingStatus}
          >
            Complete Payment
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

export default NCHLQR;
